import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CHARLIE } from "../lib/api";

const FACTS = [
  { k: "Written for", v: "Readers ages 4–8 (and the grown-ups alongside)" },
  { k: "Debuts in", v: "Book One — Love Well" },
  { k: "Voice", v: "Quiet, curious, honest — never precocious" },
  { k: "Carries", v: "A thank-you rock in his left pocket" },
  { k: "Wears", v: "Blue overalls, oversized green glasses" },
];

const BEATS = [
  {
    n: "01",
    title: "He notices before he speaks.",
    body: "When Charlie feels his tummy tighten, he tells us — and that's how the reading child learns that noticing is the first step.",
  },
  {
    n: "02",
    title: "He asks the question children carry silently.",
    body: "In Book One, Charlie asks his mother why his father doesn't come. Somewhere, a child reading this feels less alone for having wondered the same thing.",
  },
  {
    n: "03",
    title: "He receives, integrates, and offers.",
    body: "He learns something hard from a grown-up who loves him. Then he offers it to a friend who needs it. This is the arc of every book in the series.",
  },
];

export default function About() {
  return (
    <>
      <section data-testid="about-hero" className="pt-32 md:pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="italic-serif text-sm text-charcoal/60 mb-4">A character study</div>
            <h1 className="serif text-[clamp(3rem,8vw,7.5rem)] leading-[0.98] tracking-tight text-espresso">
              Meet <span className="italic text-[#4A7C7B]">Charlie.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-charcoal/90">
              Charlie is a small owl with a quiet inner voice, written for children learning how to name what they feel. He doesn't fix things or explain them away. He notices — the tight tummy, the fork that won't lift, the legs that itch in the car — and gives what he notices a name a child can hold.
            </p>
          </div>
          <div className="md:col-span-5">
            <dl className="grid gap-3">
              {FACTS.map((f) => (
                <div key={f.k} className="grid grid-cols-[9rem_1fr] items-baseline gap-4 border-b border-black/10 py-3">
                  <dt className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">{f.k}</dt>
                  <dd className="serif text-lg text-espresso">{f.v}</dd>
                </div>
              ))}
              <p className="italic-serif text-sm text-charcoal/70 leading-relaxed pt-2 max-w-md">
                Charlie's thank-you rock plays a small role in Book One and a bigger one in <span className="italic">Being Thankful</span>, Book Two.
              </p>
            </dl>
          </div>
        </div>
      </section>

      {/* Sketchbook / video moment */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-6 md:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="md:col-span-7"
          >
            <div className="torn-frame bg-[#FFFDF9] overflow-hidden">
              <img src={CHARLIE.sketches} alt="Charlie sketches by Zuzana Svobodová" className="w-full h-full object-cover" />
            </div>
            <div className="italic-serif text-xs text-charcoal/55 mt-4">
              Illustrations by Zuzana Svobodová
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="md:col-span-5 flex flex-col justify-between"
          >
            <div>
              <div className="chapter-number text-[8rem] md:text-[12rem] leading-none">i.</div>
              <h2 className="serif text-3xl md:text-4xl mt-2 leading-tight text-espresso">
                Character exploration.
              </h2>
              <p className="mt-4 text-charcoal/90 leading-relaxed max-w-md">
                From first thumbnail to Charlie's final feather. A page from the artist's sketchbook — the shape of a companion for children, arriving slowly, on purpose.
              </p>
            </div>
            <a
              href={CHARLIE.video}
              target="_blank" rel="noreferrer"
              className="mt-10 link-draw text-sm mono tracking-[0.24em] uppercase text-[#4A7C7B]"
              data-testid="watch-speed-drawing"
            >
              Watch the speed drawing →
            </a>
          </motion.div>
        </div>
      </section>

      {/* What Charlie does */}
      <section data-testid="what-charlie-does" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
            <div className="md:col-span-8">
              <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">Three beats</div>
              <h2 className="serif text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.02] tracking-tight text-espresso">
                What Charlie <span className="italic text-[#4A7C7B]">does.</span>
              </h2>
            </div>
            <div className="md:col-span-4 text-charcoal/90 text-sm md:text-base leading-relaxed">
              The small, repeatable movement inside every book in the series.
            </div>
          </div>

          <ol className="grid md:grid-cols-3 gap-x-10 gap-y-14">
            {BEATS.map((b, i) => (
              <motion.li
                key={b.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="grid grid-cols-[auto_1fr] gap-6 items-start"
              >
                <div className="chapter-number text-[4rem] md:text-[5.5rem] leading-none">{b.n}</div>
                <div>
                  <h3 className="serif text-xl md:text-2xl leading-[1.2] text-espresso">{b.title}</h3>
                  <p className="mt-3 text-charcoal/90 leading-relaxed text-sm md:text-base">{b.body}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* Manifesto quote */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <blockquote className="italic-serif text-[clamp(2rem,5vw,4rem)] leading-[1.1] text-espresso max-w-4xl">
            "Charlie is what a child needs a grown-up to become: a quiet witness. Not the loud voice that fixes it — the steady one that stays."
          </blockquote>
          <div className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B] mt-10">— from the series bible</div>
        </div>
      </section>

      {/* Waitlist CTA */}
      <section data-testid="about-cta" className="py-24 md:py-32">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <h3 className="serif text-[clamp(1.75rem,4vw,3rem)] leading-[1.1] tracking-tight text-espresso">
            Charlie's first story arrives <span className="italic text-[#4A7C7B]">soon.</span>
          </h3>
          <div className="mt-8 flex justify-center">
            <Link
              to="/waitlist"
              data-testid="about-join-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-[#2C241B] text-[#FAF8F5] pl-6 pr-4 py-3.5 hover:bg-[#4A7C7B] transition-colors"
            >
              Join the waitlist for Book One
              <span className="w-8 h-8 grid place-items-center rounded-full bg-[#FAF8F5]/15 group-hover:bg-[#FAF8F5]/25 transition">
                <ArrowRight size={16} />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
